import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://kzrlfiacamsmvqsgvlpn.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt6cmxmaWFjYW1zbXZxc2d2bHBuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk4MTk5NDMsImV4cCI6MjA2NTM5NTk0M30';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Helper functions for database operations
export class QuestionnaireAPI {
  
  /**
   * Start a new questionnaire session
   * @param {string} sessionId - Unique session identifier
   * @returns {Promise<string>} - The created session ID
   */
  static async startSession(sessionId) {
    const { data, error } = await supabase
      .from('questionnaire_sessions')
      .insert({
        session_id: sessionId,
        status: 'initial',
      })
      .select('session_id')
      .single();

    if (error) {
      throw new Error(`Failed to start session: ${error.message}`);
    }

    return data.session_id;
  }

  /**
   * Save initial questionnaire responses
   * @param {string} sessionId - Session identifier
   * @param {Object} clientInfo - Client information
   * @param {Object} initialResponses - Initial questionnaire responses
   */
  static async saveInitialResponses(sessionId, clientInfo, initialResponses) {
    const updateData = {};
    
    if (clientInfo) {
      updateData.client_info = clientInfo;
    }
    
    if (initialResponses) {
      updateData.initial_responses = initialResponses;
    }
    
    updateData.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from('questionnaire_sessions')
      .update(updateData)
      .eq('session_id', sessionId);

    if (error) {
      throw new Error(`Failed to save initial responses: ${error.message}`);
    }
  }

  /**
   * Get follow-up questions for a session
   * @param {string} sessionId - Session identifier
   * @returns {Promise<Array>} - Array of follow-up questions
   */
  static async getFollowupQuestions(sessionId) {
    const { data, error } = await supabase
      .from('questionnaire_sessions')
      .select('followup_questions')
      .eq('session_id', sessionId)
      .single();

    if (error) {
      throw new Error(`Failed to get follow-up questions: ${error.message}`);
    }

    return data.followup_questions || [];
  }

  /**
   * Save follow-up question responses
   * @param {string} sessionId - Session identifier
   * @param {Array} followupResponses - Follow-up question responses
   */
  static async saveFollowupResponses(sessionId, followupResponses) {
    const { error } = await supabase
      .from('questionnaire_sessions')
      .update({
        followup_responses: followupResponses,
        status: 'completed',
        updated_at: new Date().toISOString(),
      })
      .eq('session_id', sessionId);

    if (error) {
      throw new Error(`Failed to save follow-up responses: ${error.message}`);
    }
  }

  /**
   * Update follow-up questions (typically called by webhook)
   * @param {string} sessionId - Session identifier
   * @param {Array} followupQuestions - Generated follow-up questions
   */
  static async updateFollowupQuestions(sessionId, followupQuestions) {
    const { error } = await supabase
      .from('questionnaire_sessions')
      .update({
        followup_questions: followupQuestions,
        status: 'followup',
        updated_at: new Date().toISOString(),
      })
      .eq('session_id', sessionId);

    if (error) {
      throw new Error(`Failed to update follow-up questions: ${error.message}`);
    }
  }

  /**
   * Update final prompt (typically called by webhook)
   * @param {string} sessionId - Session identifier
   * @param {string} finalPrompt - Generated development prompt
   */
  static async updateFinalPrompt(sessionId, finalPrompt) {
    const { error } = await supabase
      .from('questionnaire_sessions')
      .update({
        final_prompt: finalPrompt,
        status: 'completed',
        updated_at: new Date().toISOString(),
      })
      .eq('session_id', sessionId);

    if (error) {
      throw new Error(`Failed to update final prompt: ${error.message}`);
    }
  }

  /**
   * Get session status and data
   * @param {string} sessionId - Session identifier
   * @returns {Promise<Object>} - Complete session data
   */
  static async getSession(sessionId) {
    const { data, error } = await supabase
      .from('questionnaire_sessions')
      .select('*')
      .eq('session_id', sessionId)
      .single();

    if (error) {
      throw new Error(`Failed to get session: ${error.message}`);
    }

    return data;
  }

  /**
   * Finalize questionnaire (placeholder for future webhook trigger)
   * @param {string} sessionId - Session identifier
   */
  static async finalizquestionnaire(sessionId) {
    // This could trigger a webhook to n8n in the future
    // For now, just update the status
    const { error } = await supabase
      .from('questionnaire_sessions')
      .update({
        status: 'processing',
        updated_at: new Date().toISOString(),
      })
      .eq('session_id', sessionId);

    if (error) {
      throw new Error(`Failed to finalize questionnaire: ${error.message}`);
    }

    console.log('✅ Questionnaire finalized, ready for n8n processing');
    return sessionId;
  }
} 